import { parseLivePath } from '~/lib/params'

/** Carried by every object entry so a cold open can place it; never a pinned prop. */
export const PIN_IDENTITY = ['type', 'parentId']

const isPlainObject = v => !!v && typeof v === 'object' && !Array.isArray(v)

/** orc stores the layer as a JSON string; anything unreadable is an empty layer. */
export const parsePins = raw => {
	if (isPlainObject(raw)) return raw
	if (typeof raw !== 'string') return {}
	try {
		const v = JSON.parse(raw)
		return isPlainObject(v) ? v : {}
	} catch {
		return {}
	}
}

/** Where a live-prop key sits in the layer; null for a key the layer cannot hold. */
export const pinPath = key => {
	const p = parseLivePath(key)
	if (!p) return null
	if (p.scope === 'effect') return [p.effect, p.key]
	if (p.scope === 'source') return ['source_settings', p.source, p.key]
	if (p.existence) return ['objects', p.id]
	if (PIN_IDENTITY.includes(p.key)) return null
	return ['objects', p.id, p.key]
}

/** The layer as live-prop keys, the vocabulary `isPinned` and the fade engine read. An object's own key precedes its props. */
export const pinKeysOf = layer => {
	const out = []
	for (const top in layer) {
		const bag = layer[top]
		if (!isPlainObject(bag)) continue
		if (top === 'source_settings') {
			for (const src in bag) if (isPlainObject(bag[src])) for (const k in bag[src]) out.push(`${top}.${src}.${k}`)
			continue
		}
		if (top !== 'objects') {
			for (const k in bag) out.push(`${top}.${k}`)
			continue
		}
		for (const id in bag) {
			out.push(`objects.${id}`)
			for (const k in bag[id]) if (!PIN_IDENTITY.includes(k)) out.push(`objects.${id}.${k}`)
		}
	}
	return out
}

/** Pin `key` in place (an Immer draft). Pinning a prop pins its object; a new leaf holds null until its value is read. */
export const addPin = (layer, key) => {
	const path = pinPath(key)
	if (!path) return
	let node = layer
	for (let i = 0; i < path.length - 1; i++) {
		if (!isPlainObject(node[path[i]])) node[path[i]] = {}
		node = node[path[i]]
	}
	const leaf = path[path.length - 1]
	// An object's existence is a bag, never a value.
	if (path.length === 2 && path[0] === 'objects') node[leaf] ??= {}
	else if (!(leaf in node)) node[leaf] = null
}

/** Unpin `key` in place. Unpinning an object takes every prop on it; unpinning a prop leaves the object pinned. */
export const removePin = (layer, key) => {
	const path = pinPath(key)
	if (!path) return
	const nodes = [layer]
	for (let i = 0; i < path.length - 1; i++) {
		const next = nodes[i][path[i]]
		if (!isPlainObject(next)) return
		nodes.push(next)
	}
	delete nodes[path.length - 1][path[path.length - 1]]
	// Empty bags go, down to the object entry, which is existence and stays.
	for (let i = path.length - 2; i >= 0; i--) {
		if (path[0] === 'objects' && i === 1) break
		if (Object.keys(nodes[i + 1]).length) break
		delete nodes[i][path[i]]
	}
}

/** A layer pinning `keys`, values unread. */
export const pinLayerOf = keys => {
	const layer = {}
	for (const key of keys) addPin(layer, key)
	return layer
}

/** The value the layer holds for `key`; undefined when it holds none. */
export const pinValue = (layer, key) => {
	const path = pinPath(key)
	if (!path) return undefined
	let node = layer
	for (const part of path) {
		if (!isPlainObject(node)) return undefined
		node = node[part]
	}
	return node ?? undefined
}
